import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@mui/material';
import SimpleDialog from 'core/components/Dialogs/SimpleDialog';
import { SWR_PRODUCT_TAGS_ENDPOINT } from 'hooks/swr/useTags';
import { isArray, isNil } from 'lodash';
import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import * as tagsService from 'services/tagService';
import { mutate } from 'swr';
import { object, string } from 'yup';
import type { Tag } from 'models/tag';
import type { FC } from 'react';

interface TagDialogProps {
  open: boolean;
  tag?: Tag;
  onClose: () => void;
}

type TagFormData = Pick<Tag, 'name'>;

const TagDialog: FC<TagDialogProps> = ({ open, tag, onClose }) => {
  const isNew = isNil(tag);

  const schema = object().shape({
    name: string().max(50).required(),
  });

  const formContext = useForm<TagFormData>({
    mode: 'onTouched',
    defaultValues: tag,
    resolver: yupResolver(schema),
  });

  useEffect(() => {
    formContext.reset({
      name: tag?.name ?? '',
    });
  }, [formContext, tag, open]);

  const resetTagsCache = async (): Promise<unknown> =>
    await mutate(
      (key) => isArray(key) && key[0] === SWR_PRODUCT_TAGS_ENDPOINT,
      undefined
    );

  const saveData = useCallback(
    async (data: TagFormData) => {
      if (isNil(tag)) {
        await tagsService.createTagAsync(data.name);
      } else {
        await tagsService.updateTagAsync(tag.id, data.name);
      }

      await resetTagsCache();

      onClose();
    },
    [tag, onClose]
  );

  return (
    <SimpleDialog
      open={open}
      onClose={onClose}
      title={isNew ? 'Создание тега' : 'Редактирование тега'}
      maxWidth='sm'
      fullWidth
      content={
        <FormContainer formContext={formContext} onSuccess={saveData}>
          <TextFieldElement
            fullWidth
            margin='normal'
            label='Название'
            name='name'
            required
          />
        </FormContainer>
      }
      actions={
        <>
          <Button
            onClick={() => {
              void formContext.handleSubmit(saveData)();
            }}>
            {isNew ? 'Добавить' : 'Сохранить'}
          </Button>
          <Button onClick={onClose}>Закрыть</Button>
        </>
      }
    />
  );
};

export default TagDialog;
